import { Component, Input, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroupDirective,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton, MatIconButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import moment from 'moment';

import { CvReviewEntry, CvReviewSection } from './cv-import-review.model';
import { CvIndustriesFieldComponent } from './fields/cv-industries-field.component';
import { CvTasksFieldComponent } from './fields/cv-tasks-field.component';
import { FaehigkeitenService } from '../../services/faehigkeiten.service';
import { RequestsService } from '../../services/requests.service';
import { IFaehigkeit } from '../../shared/model/faehigkeit.model';
import { Requests, RequestType } from '../../shared/model/request.model';

/** Shows validation errors immediately (not only after the field is touched). */
export class ImmediateErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    _form: FormGroupDirective | NgForm | null,
  ): boolean {
    return !!control && control.invalid;
  }
}

@Component({
  selector: 'app-cv-section-review',
  templateUrl: './cv-section-review.component.html',
  styleUrl: './cv-section-review.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIcon,
    MatSlideToggle,
    MatCheckbox,
    MatButton,
    MatIconButton,
    TranslatePipe,
    CvIndustriesFieldComponent,
    CvTasksFieldComponent,
  ],
  standalone: true,
})
export class CvSectionReviewComponent implements OnInit {
  @Input() section!: CvReviewSection;

  readonly errorMatcher = new ImmediateErrorStateMatcher();
  readonly maxBirthdate = moment().endOf('day');

  skillList: IFaehigkeit[] = [];
  industries: Requests[] = [];

  constructor(
    private faehigkeitenService: FaehigkeitenService,
    private requestsService: RequestsService,
  ) {}

  ngOnInit(): void {
    const types = new Set(this.section.fields.map((field) => field.type));
    if (types.has('tasks')) {
      this.faehigkeitenService.query().subscribe({
        next: (response) => (this.skillList = response.content ?? []),
      });
    }
    if (types.has('industries')) {
      this.requestsService
        .getRequests(0, 300, RequestType.INDUSTRY, true)
        .subscribe({
          next: (response) => (this.industries = response.content ?? []),
        });
    }
  }

  control(entry: CvReviewEntry, key: string): FormControl {
    return entry.form.get(key) as FormControl;
  }

  clear(entry: CvReviewEntry, key: string): void {
    const control = this.control(entry, key);
    control.setValue(null);
    control.markAsDirty();
  }

  isInvalidAccepted(entry: CvReviewEntry): boolean {
    return entry.accepted && entry.form.invalid;
  }

  acceptedCount(): number {
    return this.section.entries.filter((entry) => entry.accepted).length;
  }

  setAll(accepted: boolean): void {
    this.section.entries.forEach((entry) => (entry.accepted = accepted));
  }

  confidencePercent(entry: CvReviewEntry): number | null {
    return entry.confidence == null ? null : Math.round(entry.confidence * 100);
  }
}
